import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_CONFIG, exportCanvas, renderBackground } from './render';
import type { BackgroundConfig, ImageFormat, RenderResult } from './types';

export interface UseBackgroundOptions {
  /** Se fusiona sobre DEFAULT_CONFIG. Solo fija el estado inicial. */
  initialConfig?: Partial<BackgroundConfig>;
  /** Se dispara tras cada render, con paleta, auditoría y contraste. */
  onRender?: (result: RenderResult) => void;
}

export interface UseBackgroundReturn {
  /** Asignar al <canvas>. */
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  config: BackgroundConfig;
  /** Cambia una clave con el tipo correcto: set('hue', 210). */
  set: <K extends keyof BackgroundConfig>(key: K, value: BackgroundConfig[K]) => void;
  /** Cambio de varias claves a la vez, en un solo render. */
  patch: (values: Partial<BackgroundConfig>) => void;
  /** null hasta el primer render. */
  result: RenderResult | null;
  /** Data URL del lienzo y su peso real en KB. */
  toDataUrl: (format?: ImageFormat, quality?: number) => { url: string; kb: number };
  /**
   * El lienzo como fichero, para subirlo.
   *
   * `toBlob` y no `toDataUrl`: el base64 infla los bytes un 33 % y luego hay que
   * deshacerlo para mandarlo. Aquí sale ya binario, que es lo que viaja.
   *
   * Es asíncrono porque el navegador codifica fuera del hilo principal — con
   * PNG sin pérdida a 1080×1920 se nota, y bloquear la interfaz justo al pulsar
   * «usar este fondo» es el peor momento.
   */
  toBlob: (format?: ImageFormat, quality?: number) => Promise<Blob | null>;
  download: (format?: ImageFormat, quality?: number) => number;
}

/**
 * Toda la lógica del generador, sin interfaz. Útil para montar controles
 * propios o incrustar el lienzo en otro diseño:
 *
 *   const { canvasRef, set, result } = useBackground({ initialConfig: { hue: 20 } });
 *   return <canvas ref={canvasRef} />;
 */
export function useBackground({ initialConfig, onRender }: UseBackgroundOptions = {}): UseBackgroundReturn {
  const [config, setConfig] = useState<BackgroundConfig>({ ...DEFAULT_CONFIG, ...initialConfig });
  const [result, setResult] = useState<RenderResult | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  /* El callback vive en un ref para no entrar en las dependencias del efecto:
     así una lambda inline no provoca renders extra. */
  const onRenderRef = useRef(onRender);
  onRenderRef.current = onRender;

  const set = useCallback(
    <K extends keyof BackgroundConfig>(key: K, value: BackgroundConfig[K]) =>
      setConfig(c => ({ ...c, [key]: value })),
    []
  );
  const patch = useCallback(
    (values: Partial<BackgroundConfig>) => setConfig(c => ({ ...c, ...values })),
    []
  );

  /* Un render por frame: los sliders emiten decenas de eventos seguidos y las
     tramas densas a 2K cuestan varios ms. El frame pendiente se cancela. */
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (!canvasRef.current) return;
      const res = renderBackground(canvasRef.current, config);
      setResult(res);
      onRenderRef.current?.(res);
    });
    return () => cancelAnimationFrame(id);
  }, [config]);

  const toDataUrl = useCallback(
    (format: ImageFormat = 'image/png', quality = 0.82) => {
      if (!canvasRef.current) return { url: '', kb: 0 };
      return exportCanvas(canvasRef.current, format, quality);
    },
    []
  );

  /** Dispara la descarga y devuelve el peso en KB del archivo generado. */
  const toBlob = useCallback(
    (format: ImageFormat = 'image/png', quality = 0.92) =>
      new Promise<Blob | null>((resolve) => {
        const canvas = canvasRef.current;
        if (!canvas) {
          resolve(null);
          return;
        }
        canvas.toBlob((b) => resolve(b), format, quality);
      }),
    [],
  );

  const download = useCallback(
    (format: ImageFormat = 'image/png', quality = 0.82) => {
      const { url, kb } = toDataUrl(format, quality);
      if (!url) return 0;
      const ext = { 'image/png': 'png', 'image/webp': 'webp', 'image/jpeg': 'jpg' }[format];
      const a = document.createElement('a');
      a.href = url;
      a.download = `fondo-${config.seed}-${config.gtype}.${ext}`;
      a.click();
      return kb;
    },
    [toDataUrl, config.seed, config.gtype]
  );

  return { canvasRef, config, set, patch, result, toDataUrl, toBlob, download };
}
